import { UUID } from "../../../core/pojo/id-variable";
import { MetricSharedLinkContact, MetricSharedLinkWrapper, SharedLinkPeriods } from "./MetricSharedLinkWrapper";
import { LocalDate, LocalMonthDate } from "../../../core/pojo/date-variable";
import {
  ModalMetricPublicSharedLinkFormComponent,
} from "../components/modal-public-link/form/modal-metric-public-shared-link-form.component";
import { DateService } from "../../../util/common/date-service/date.service";
import { KpiRecipient } from "./KpiMember";
import { Organization } from "../../organization/dto/Organization";
import { MetricPeriodWrapper } from "./MetricGroupsAndPeriods";
import { LocalDateTime } from "aws-sdk/clients/forecastservice";
import { MetricGroup } from "./MetricGroup";
import { ShowGroupingWrapperKpiBulkRequest } from "../service/internal/track-untrack-grouping-helper";

export class MetricBulkRequestCompanyWrapper {
  company: MetricBulkRequestCompany;
  link: MetricSharedLinkWrapper;
  linkForm?: ModalMetricPublicSharedLinkFormComponent; // will be set after receiving this object from db.
  periods: MetricBulkRequestCompanyPeriodWrapper;
  latestNotificationData?: MetricSharedLinkNotificationDataDTO;

  /**
   * Performs multiple checks:
   * 1) if there is a msl:
   *    a) activate the periods selectors, if there is at least one period. By default, they are disabled (backwards compability).
   *    b) by default, there is an auto-selected till period. We don't want it to be auto-selected.
   *    So if it was not selected before, it is reset.
   * 2) if there is no msl: reset all controls.
   */
  checkAfterInitValues(): void {
    if (this.linkForm.form.value.uuid) {
      if (this.linkForm.actualPeriods.all.length) {
        this.linkForm.form.controls.actual.controls.isActivated.patchValue(true);
      }
      if (this.linkForm.budgetPeriods.all.length) {
        this.linkForm.form.controls.budget.controls.isActivated.patchValue(true);
      }
      if (!this.linkForm.getFormRawValue().actual.fromPeriod) {
        this.linkForm.form.controls.actual.controls.tillPeriod.patchValue(null);
      }
      if (!this.linkForm.getFormRawValue().budget.fromPeriod) {
        this.linkForm.form.controls.budget.controls.tillPeriod.patchValue(null);
      }
    } else {
      this.linkForm.form.controls.actual.controls.isActivated.patchValue(false);
      this.linkForm.form.controls.budget.controls.isActivated.patchValue(false);
      this.linkForm.form.controls.actual.controls.tillPeriod.patchValue(null);
      this.linkForm.form.controls.budget.controls.tillPeriod.patchValue(null);
    }
  }

  static deserialize(object: MetricBulkRequestCompanyWrapper, fetchedPeriods: LocalDate[], allPeriods: LocalDate[]): MetricBulkRequestCompanyWrapper {
    const wrapper: MetricBulkRequestCompanyWrapper = Object.assign(new MetricBulkRequestCompanyWrapper(), object);
    this.setCompanyWrappersMslForm(wrapper, allPeriods);
    this.fillMissingPeriods(fetchedPeriods, wrapper);
    wrapper.checkAfterInitValues();

    return wrapper;
  }

  static setCompanyWrappersMslForm(wrapper: MetricBulkRequestCompanyWrapper, uniquePeriods: LocalDate[]): void {
    wrapper.linkForm = new ModalMetricPublicSharedLinkFormComponent();
    const periods: LocalDate[] = uniquePeriods.map(DateService.toLocalMonthDate);
    const actual: SharedLinkPeriods = {
      from: periods,
      till: periods,
      all: periods,
    };
    const budget: SharedLinkPeriods = {
      from: periods,
      till: periods,
      all: periods,
    };
    const formPeriods = {
      actual,
      budget,
    };
    wrapper.linkForm.initializeValues(wrapper.company.uuid, formPeriods, wrapper.link, null);
  }


  private static fillMissingPeriods(periods: LocalDate[], wrapper: MetricBulkRequestCompanyWrapper): void {
    const newActualPeriods: MetricBulkRequestCompanyPeriod[] = [];
    const newBudgetPeriods: MetricBulkRequestCompanyPeriod[] = [];
    let actualIndex = 0;
    let budgetIndex = 0;

    for (const period of periods) {
      while (actualIndex < wrapper.periods.actual.length && wrapper.periods.actual[actualIndex].period < period) {
        newActualPeriods.push(wrapper.periods.actual[actualIndex]);
        actualIndex++;
      }
      if (actualIndex >= wrapper.periods.actual.length || wrapper.periods.actual[actualIndex].period !== period) {
        newActualPeriods.push({
          period: period,
          isPersisted: false,
          isEmpty: true,
          isRequested: wrapper.link !== null && wrapper.link.actual.isActivated && wrapper.link.actual.fromPeriod < period && wrapper.link.actual.tillPeriod > period,
        });
      } else {
        newActualPeriods.push(wrapper.periods.actual[actualIndex]);
        actualIndex++;
      }

      while (budgetIndex < wrapper.periods.budget.length && wrapper.periods.budget[budgetIndex].period < period) {
        newBudgetPeriods.push(wrapper.periods.budget[budgetIndex]);
        budgetIndex++;
      }
      if (budgetIndex >= wrapper.periods.budget.length || wrapper.periods.budget[budgetIndex].period !== period) {
        newBudgetPeriods.push({
          period: period,
          isPersisted: false,
          isEmpty: true,
          isRequested: wrapper.link !== null && wrapper.link.budget.isActivated && wrapper.link.budget.fromPeriod < period && wrapper.link.budget.tillPeriod > period,
        });
      } else {
        newBudgetPeriods.push(wrapper.periods.budget[budgetIndex]);
        budgetIndex++;
      }
    }

    wrapper.periods.actual = newActualPeriods;
    wrapper.periods.budget = newBudgetPeriods;
  }
}

export class MetricSharedLinkNotificationDataDTO {
  creationTime: LocalDateTime;
  recipientsNames: string[];
  submissionTime?: LocalDateTime;
}

export class MetricBulkRequestCompany {
  uuid: UUID;
  name: string;
  companyMembers: KpiRecipient[];
  isTracked: boolean;
  visibleMetricAmount: number;
}

export class MetricBulkRequestCompanyPeriodWrapper {
  actual: MetricBulkRequestCompanyPeriod[];
  budget: MetricBulkRequestCompanyPeriod[];
}

export class MetricBulkRequestCompanyPeriod {
  period: LocalDate;
  isPersisted: boolean;
  isEmpty: boolean; // has at least one metric data for a given period.
  isRequested: boolean; // is part of metric shared link request.
}

export class MetricBulkRequestDto {
  memberUUIDs: UUID[];
  customText: string;
}

export class MetricBulkRequestTestEmailDTO {
  email: string;
  customText: string;
}

export class ShowKpiBulkRequest {
  grouping = new ShowGroupingWrapperKpiBulkRequest();
  updateMetricsModal = false;
  managePeriodsModal = false;
  kpiRequestModal = false;
  bulkMslModal = false;
  deletePeriodsModal = false;
  deleteAllMSLModal = false;
  createAllMSLModal = false;
  deleteOneMSLModal = false;
  // it is very costly to load all data after every change. This variable will show button to update the changes by click.
  updateTableButton = false;
  tooltip: {
    tooltipCoordinates: DOMRect;
    companyWrapper?: MetricBulkRequestCompanyPeriod;
    pointOfContact?: MetricSharedLinkContact;
    pointOfContactInsufficiency?: boolean;
  };

  allMslTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  requestRecipientsTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
    titleText: string;
  };

  requestSentTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  requestReceivedTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  companyNameTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  copyCredentialsTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  copyPasswordTooltip:{
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  copyLinkTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
  };

  pocTooltip: {
    tooltipCoordinates: DOMRect;
    tooltipText: string;
    tooltipTitle: string;
  };
}

export type PeriodRoleKpiBulkRequest = keyof MetricBulkRequestCompanyPeriodWrapper;
export type MetricManageRowActionKpiBulkRequest = 'add periods' | 'delete periods'

export interface MetricManageRowKpiBulkRequest {
  iconAndStyling: string;
  name: string;
  action: MetricManageRowActionKpiBulkRequest;
  nameStyling?: string;
}

export class IsKpiBulkRequest {
  admin = false;
  collapsed = false;
}

export class SelectedKpiBulkRequest {
  org: Organization;
  companyWrapper: MetricBulkRequestCompanyWrapper;
  companiesForManageMetricsModal: MetricBulkRequestCompanyWrapper[];
  periodToScrollOnInit: LocalMonthDate;
  companiesForManagePeriodsModal: MetricBulkRequestCompany[];
  mslFormForContactUpdate: MetricBulkRequestCompanyWrapper;
  companyMetricGroups: MetricGroup[];
  periodsForManagePeriodsModal: MetricPeriodWrapper = {
    actual: [],
    budget: [],
  };

  periodsForDeletePeriodsModal: MetricPeriodWrapper = {
    actual: [],
    budget: [],
  };
}

export class BulkRequestFetchPeriodsResponse {
  companyUuid: UUID;
  periods: MetricBulkRequestCompanyPeriodWrapper;
}
